import { useState } from "react";
import { AlertCircle, KeyRound, Loader2, Smartphone } from "lucide-react";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";

// The sign-in code step, shared by the password login (Login.jsx) and the Google
// return (AuthCallback.jsx) — both reach the same point: identity proven, second
// factor still owed. Keeping one component means the backup-code path can't
// exist on one route and be missing on the other, which is exactly how someone
// with a dead phone ends up locked out on whichever page they happened to use.
//
// `onSubmit(code)` should reject on failure; the input is cleared either way so a
// wrong code isn't re-submitted by a second Enter press.
export default function TwoFactorCodeForm({ onSubmit, onCancel, cancelLabel = "← Back to sign in" }) {
  const [mode, setMode] = useState("app"); // "app" | "backup"
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isBackup = mode === "backup";
  // TOTP is exactly 6 digits; a backup code is 10 characters shown as XXXXX-XXXXX
  // but accepted in any casing, with or without the dash (the server normalises).
  const ready = isBackup ? code.replace(/[^A-Za-z0-9]/g, "").length >= 10 : code.length === 6;

  function switchMode(next) {
    setMode(next);
    setCode("");
    setError("");
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onSubmit(code);
    } catch (err) {
      setError((err && err.message) || "That code didn't work. Try again.");
      // Clear on failure: the value is wrong, and leaving it selected invites a
      // second identical submission against the attempt budget.
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {error && (
        <div role="alert" className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* toggle */}
      <div className="inline-flex w-full rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-white/10 dark:bg-white/5">
        {[
          { k: "app", label: "Authenticator app", icon: Smartphone },
          { k: "backup", label: "Backup code", icon: KeyRound },
        ].map((opt) => (
          <button
            key={opt.k}
            type="button"
            onClick={() => switchMode(opt.k)}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
              mode === opt.k
                ? "bg-white text-brand-600 shadow-sm dark:bg-white/10 dark:text-brand-300"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            )}
          >
            <opt.icon size={15} /> {opt.label}
          </button>
        ))}
      </div>

      {isBackup ? (
        <>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Enter one of the backup codes you saved when you turned on two-factor authentication.
          </p>
          <Input
            id="twofa-backup"
            label="Backup code"
            placeholder="ABCDE-FGHJK"
            value={code}
            autoComplete="one-time-code"
            spellCheck="false"
            maxLength={13}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoFocus
          />
          <p className="text-xs text-slate-400">Each code works once. Using one here uses it up.</p>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Enter the 6-digit code from your authenticator app.
          </p>
          {/* inputMode/autoComplete/id: without them a phone opened the full
              alphabetic keyboard for a numeric code, the OS couldn't offer the
              code to autofill, and the label pointed at no field. */}
          <Input
            id="twofa-code"
            label="Authentication code"
            placeholder="123456"
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            autoFocus
          />
        </>
      )}

      <Button type="submit" className="w-full" disabled={loading || !ready}>
        {loading ? <Loader2 size={16} className="animate-spin" /> : "Verify & sign in"}
      </Button>

      {onCancel && (
        <button type="button" onClick={onCancel} className="w-full text-center text-sm text-slate-400 hover:text-slate-600">
          {cancelLabel}
        </button>
      )}
    </form>
  );
}
